import { useState, useEffect } from "react";
import useTranslation from "../useTranslation";
import { useCompanies } from "../../context/CompanyContext";
import { getNotConnectingBases } from "../../api/baseApi";
import { toast } from "react-toastify";

export const useProblematicDevicesManagement = () => {
  const { t } = useTranslation("productManagerPage");
  const { companies, loading: companiesLoading } = useCompanies();

  // Problematic devices states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [problematicDevices, setProblematicDevices] = useState([]);
  const [filteredProblematicDevices, setFilteredProblematicDevices] = useState(
    []
  );
  const [loadingProblematicDevices, setLoadingProblematicDevices] =
    useState(false);

  // Functions for problematic devices
  const fetchProblematicDevices = async () => {
    try {
      setLoadingProblematicDevices(true);

      const filters = {};
      if (selectedCompany) {
        filters.companyid = selectedCompany;
      }
      // Fetch all problematic devices from the API
      const data = await getNotConnectingBases(filters);
      setProblematicDevices(data);
      setFilteredProblematicDevices(data);
    } catch (error) {
      console.error("Error fetching problematic devices:", error);
      setProblematicDevices([]);
      setFilteredProblematicDevices([]);
    } finally {
      setLoadingProblematicDevices(false);
    }
  };

  // Function to apply filters -- only search term is currently used
  const applyFilters = () => {
    try {
      const newFilteredProblematicDevices = problematicDevices
        .filter((device) => {
          if (searchTerm && searchTerm.trim() !== "") {
            const search = searchTerm.toLowerCase().trim();

            // Check across all fields
            const matchesSearch =
              // Check baseid
              (device.baseid && device.baseid.toLowerCase().includes(search)) ||
              // Check company name
              (device.companyname &&
                device.companyname &&
                device.companyname.toLowerCase().includes(search)) ||
              // Check status
              (device.status && device.status.toLowerCase().includes(search)) ||
              // Chech logoutdate
              (device.logoutdate && device.logoutdate.includes(search));

            return matchesSearch;
          }
          return true; // No search term, return all devices
        })
        .sort((a, b) => a._id - b._id);

      // Only update if there are changes
      setFilteredProblematicDevices(newFilteredProblematicDevices);
    } catch (error) {
      console.error("Error applying filters:", error);
      setFilteredProblematicDevices([]);
    } finally {
      setLoadingProblematicDevices(false);
    }
  };

  // Effect to fetch problematic devices on mount or when filters change
  useEffect(() => {
    fetchProblematicDevices();
  }, [selectedCompany]);

  useEffect(() => {
    // Reset to page 1 when recordsPerPage changes
    setCurrentPage(1);
  }, [recordsPerPage]);

  // Effect to apply filters when search term or problematic devices change
  useEffect(() => {
    applyFilters();
  }, [searchTerm, problematicDevices]);

  // Calculate pagination
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const paginatedProblematicDevices = filteredProblematicDevices.slice(
    startIndex,
    endIndex
  );

  return {
    // State,
    companies,
    searchTerm,
    setSearchTerm,
    selectedCompany,
    setSelectedCompany,
    // Pagination states
    recordsPerPage,
    setRecordsPerPage,
    currentPage,
    setCurrentPage,
    paginatedProblematicDevices,
    // Problematic devices states
    problematicDevices,
    filteredProblematicDevices,
    loadingProblematicDevices,
    applyFilters,
  };
};
